import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import type { RevaluationItem } from '@/types/revaluation';
import { RotateCcw, Edit3 } from 'lucide-react';

interface RevaluationBatchGridProps {
  items: RevaluationItem[];
  onUpdateItem: (itemId: string, updates: Partial<RevaluationItem>) => void;
  readonly?: boolean;
  selectedItemIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  onReapplyDefaults?: (applyToAll: boolean) => void;
  revaluationMode: string;
  revaluationAmount: number;
}

export function RevaluationBatchGrid({ 
  items, 
  onUpdateItem, 
  readonly = false, 
  selectedItemIds = new Set(), 
  onSelectionChange,
  onReapplyDefaults,
}: RevaluationBatchGridProps): React.ReactElement {
  const [showReapplyConfirm, setShowReapplyConfirm] = useState(false);
  const [reapplyMode, setReapplyMode] = useState<'all' | 'unmodified'>('all');

  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;
    if (checked) {
      onSelectionChange(new Set(items.map(item => item.id)));
    } else {
      onSelectionChange(new Set());
    }
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    if (!onSelectionChange) return;
    const newSelection = new Set(selectedItemIds);
    if (checked) {
      newSelection.add(itemId);
    } else {
      newSelection.delete(itemId);
    }
    onSelectionChange(newSelection);
  };

  const [editingValues, setEditingValues] = useState<Record<string, string>>({});
  const [focusedCell, setFocusedCell] = useState<string | null>(null);

  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleRevaluedNBVFocus = (itemId: string, currentValue: number) => {
    setFocusedCell(itemId);
    setEditingValues(prev => ({ ...prev, [itemId]: currentValue.toFixed(2) }));
  };

  const handleRevaluedNBVChange = (itemId: string, newValue: string) => {
    // Allow empty string, numbers, and one decimal point
    if (newValue === '' || /^\d*\.?\d*$/.test(newValue)) {
      setEditingValues(prev => ({ ...prev, [itemId]: newValue }));
    }
  };

  const handleRevaluedNBVBlur = (item: RevaluationItem) => {
    setFocusedCell(null);
    const newValue = editingValues[item.id];
    
    if (!newValue || newValue === '') {
      // Reset to original value if empty
      setEditingValues(prev => {
        const updated = { ...prev };
        delete updated[item.id];
        return updated;
      });
      return;
    }

    const value = parseFloat(newValue);
    if (isNaN(value)) {
      // Reset to original value if invalid
      setEditingValues(prev => {
        const updated = { ...prev };
        delete updated[item.id];
        return updated;
      });
      return;
    }

    const adjustment = value - item.currentNBV;

    onUpdateItem(item.id, {
      revaluedNBV: value,
      revaluationAdjustment: adjustment,
      isOverridden: true,
    });

    // Clear editing value
    setEditingValues(prev => {
      const updated = { ...prev };
      delete updated[item.id];
      return updated;
    });
  };

  const handleReapplyDefaults = () => {
    setShowReapplyConfirm(true);
  };

  const confirmReapplyDefaults = () => {
    if (onReapplyDefaults) {
      onReapplyDefaults(reapplyMode === 'all');
    }
    setShowReapplyConfirm(false);
  };

  const overriddenCount = items.filter(item => item.isOverridden).length;

  return (
    <TooltipProvider>
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {!readonly && overriddenCount > 0 && (
          <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 mx-6 mt-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Edit3 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-sm text-amber-900 dark:text-amber-100">
                {overriddenCount} {overriddenCount === 1 ? 'line has' : 'lines have'} manual overrides
              </span>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleReapplyDefaults}
              className="border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/30"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reapply Defaults
            </Button>
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {items.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div className="flex flex-col items-center justify-center space-y-4 py-12">
                  <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-6">
                    <Edit3 className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">No Assets Added</h3>
                    <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                      No assets added yet. Click "Add Assets" to begin.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                <TableRow className="border-t-2 border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                  {!readonly && onSelectionChange && (
                    <TableHead className="h-12 font-semibold w-[40px] pl-6">
                      <Checkbox
                        checked={selectedItemIds.size === items.length && items.length > 0}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all"
                      />
                    </TableHead>
                  )}
                  <TableHead className="h-12 font-semibold w-[120px]">Code</TableHead>
                  <TableHead className="h-12 font-semibold min-w-[250px]">Description</TableHead>
                  <TableHead className="h-12 font-semibold text-right w-[180px]">Net Book Value (£)</TableHead>
                  <TableHead className="h-12 font-semibold text-right w-[200px]">
                    Revalued Net Book Value (£)
                  </TableHead>
                  <TableHead className="h-12 font-semibold text-right w-[180px] pr-6">Revaluation Adjustment (£)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow 
                    key={item.id}
                    className={`
                      ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
                      hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800
                    `}
                  >
                      {!readonly && onSelectionChange && (
                        <TableCell className="w-[40px] pl-6">
                          <Checkbox
                            checked={selectedItemIds.has(item.id)}
                            onCheckedChange={(checked) => handleSelectItem(item.id, checked as boolean)}
                            aria-label={`Select ${item.assetCode}`}
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-medium w-[120px] text-slate-700 dark:text-slate-300">{item.assetCode}</TableCell>
                      <TableCell className="min-w-[250px] text-slate-700 dark:text-slate-300">
                        <div className="flex items-center gap-2">
                          {item.assetDescription}
                          {item.isOverridden && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                  <Edit3 className="h-3 w-3 mr-1" />
                                  Edited
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p>User override - value differs from calculated default</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right w-[180px] text-slate-700 dark:text-slate-300">{formatCurrency(item.currentNBV)}</TableCell>
                      <TableCell className="w-[200px]">
                        {readonly ? (
                          <div className="text-right text-slate-700 dark:text-slate-300">{formatCurrency(item.revaluedNBV)}</div>
                        ) : (
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={focusedCell === item.id ? (editingValues[item.id] ?? item.revaluedNBV.toFixed(2)) : formatCurrency(item.revaluedNBV)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleRevaluedNBVChange(item.id, e.target.value)}
                            onFocus={() => handleRevaluedNBVFocus(item.id, item.revaluedNBV)}
                            onBlur={() => handleRevaluedNBVBlur(item)}
                            className="text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            aria-describedby={item.isOverridden ? `override-${item.id}` : undefined}
                          />
                        )}
                      </TableCell>
                      <TableCell className={`text-right w-[180px] pr-6 font-medium ${item.revaluationAdjustment >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {item.revaluationAdjustment >= 0 ? '+' : ''}{formatCurrency(item.revaluationAdjustment)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <ConfirmationModal
        open={showReapplyConfirm}
        onOpenChange={setShowReapplyConfirm}
        onConfirm={confirmReapplyDefaults}
        title="Reapply Defaults"
        description={
          <div className="space-y-4">
            <p>This will recalculate default values from the header settings.</p>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="reapply-mode"
                  checked={reapplyMode === 'all'}
                  onChange={() => setReapplyMode('all')}
                  className="h-4 w-4"
                />
                <span className="text-sm">Apply to all lines (overwrite manual values)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="reapply-mode"
                  checked={reapplyMode === 'unmodified'}
                  onChange={() => setReapplyMode('unmodified')}
                  className="h-4 w-4"
                />
                <span className="text-sm">Apply only to unmodified lines (keep manual values)</span>
              </label>
            </div>
          </div>
        }
        confirmText="Apply"
      />
    </TooltipProvider>
  );
}
